import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Button, Select, Label, Icon } from '../../components/ui';
import { useWorkspace } from '../../contexts/WorkspaceContext';

export default function FinancialStatements() {
  const { fiscalYears, currentFiscalYear, loadFiscalYears, activeWorkspace } = useWorkspace();
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [statementType, setStatementType] = useState('balance-sheet');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeWorkspace) loadFiscalYears(activeWorkspace._id);
  }, [activeWorkspace]);

  useEffect(() => {
    if (currentFiscalYear) setFiscalYearId(currentFiscalYear._id);
  }, [currentFiscalYear]);

  const loadStatement = async () => {
    if (!fiscalYearId) { toast.error('Sélectionnez un exercice'); return; }
    try {
      setLoading(true);
      const endpoint = statementType === 'balance-sheet'
        ? '/accounting/financial-statements/balance-sheet'
        : '/accounting/financial-statements/income-statement';
      const res = await api.get(endpoint, { params: { fiscalYearId } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!fiscalYearId) return;
    try {
      const params = new URLSearchParams({ fiscalYearId });
      const endpoint = statementType === 'balance-sheet'
        ? '/accounting/export/balance-sheet'
        : '/accounting/export/income-statement';

      const res = await api.get(`${endpoint}?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = statementType === 'balance-sheet' ? 'bilan.xlsx' : 'cpc.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  const exportPdf = async () => {
    if (!fiscalYearId) return;
    try {
      const params = new URLSearchParams({ fiscalYearId, type: statementType === 'balance-sheet' ? 'balance' : 'income' });
      const res = await api.get(`/accounting/export/financial-statements/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${statementType === 'balance-sheet' ? 'bilan' : 'cpc'}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export PDF téléchargé');
    } catch { toast.error('Erreur export PDF'); }
  };

  const exportWord = async () => {
    if (!fiscalYearId) return;
    try {
      const params = new URLSearchParams({ fiscalYearId, type: statementType === 'balance-sheet' ? 'balance' : 'income' });
      const res = await api.get(`/accounting/export/financial-statements/word?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${statementType === 'balance-sheet' ? 'bilan' : 'cpc'}.docx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Word téléchargé');
    } catch { toast.error('Erreur export Word'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(v || 0);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconReportAnalytics" className="text-indigo-600 mr-3" />
          États Financiers
        </h1>
        <p className="text-sm text-gray-600 mt-1">Bilan, Compte de Résultat et États annexes.</p>
      </div>

      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Exercice</Label>
            <Select value={fiscalYearId} onChange={(e) => setFiscalYearId(e.target.value)}>
              <option value="">-- Choisir --</option>
              {fiscalYears.map((fy) => (
                <option key={fy._id} value={fy._id}>{fy.label} ({fy.year})</option>
              ))}
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">État</Label>
            <Select value={statementType} onChange={(e) => setStatementType(e.target.value)}>
              <option value="balance-sheet">Bilan</option>
              <option value="income-statement">Compte de Résultat (CPC)</option>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadStatement} disabled={loading}>
              {loading ? 'Chargement...' : 'Générer'}
            </Button>
            <Button onClick={exportExcel} variant="secondary">Excel</Button>
            <Button onClick={exportPdf} variant="secondary">PDF</Button>
            <Button onClick={exportWord} variant="secondary">Word</Button>
          </div>
        </div>
      </Card>

      {data && statementType === 'balance-sheet' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h2 className="text-lg font-bold text-gray-800 mb-4">ACTIF</h2>
            <h3 className="text-md font-semibold text-gray-700 mb-2">Actif non courant</h3>
            {(data.actif?.actifNonCourant || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code} {item.label}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Actif courant</h3>
            {(data.actif?.actifCourant || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code} {item.label}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Trésorerie</h3>
            {(data.actif?.tresorerie || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code} {item.label}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
          </Card>
          <Card>
            <h2 className="text-lg font-bold text-gray-800 mb-4">PASSIF</h2>
            <h3 className="text-md font-semibold text-gray-700 mb-2">Capitaux propres</h3>
            {(data.passif?.capitauxPropres || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code} {item.label}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Passif non courant</h3>
            {(data.passif?.passifNonCourant || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code} {item.label}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Passif courant</h3>
            {(data.passif?.passifCourant || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code} {item.label}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
          </Card>
        </div>
      )}

      {data && statementType === 'income-statement' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h2 className="text-lg font-bold text-gray-800 mb-4">PRODUITS</h2>
            <h3 className="text-md font-semibold text-gray-700 mb-2">Produits d'exploitation</h3>
            {(data.produitsExploitation || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 font-bold border-t-2 mt-2">
              <span>Total produits d'exploitation</span>
              <span>{formatCurrency(data.sousTotaux?.totalProduitsExploitation)}</span>
            </div>
            <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Produits financiers</h3>
            {(data.produitsFinanciers || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 font-bold border-t-2 mt-2">
              <span>Total produits financiers</span>
              <span>{formatCurrency(data.sousTotaux?.totalProduitsFinanciers)}</span>
            </div>
          </Card>
          <Card>
            <h2 className="text-lg font-bold text-gray-800 mb-4">CHARGES</h2>
            <h3 className="text-md font-semibold text-gray-700 mb-2">Charges d'exploitation</h3>
            {(data.chargesExploitation || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 font-bold border-t-2 mt-2">
              <span>Total charges d'exploitation</span>
              <span>{formatCurrency(data.sousTotaux?.totalChargesExploitation)}</span>
            </div>
            <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Charges financières</h3>
            {(data.chargesFinancieres || []).filter(i => i.montant).map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-b text-sm">
                <span>{item.code}</span>
                <span className="font-semibold">{formatCurrency(item.montant)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 font-bold border-t-2 mt-2">
              <span>Total charges financières</span>
              <span>{formatCurrency(data.sousTotaux?.totalChargesFinancieres)}</span>
            </div>
          </Card>
          <div className="lg:col-span-2">
            <Card className="bg-indigo-50">
              <div className="grid grid-cols-2 gap-4 text-lg">
                <div>
                  <div className="text-gray-600">Résultat d'exploitation</div>
                  <div className="font-bold">{formatCurrency(data.resultatExploitation)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Résultat financier</div>
                  <div className="font-bold">{formatCurrency(data.resultatFinancier)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Résultat exceptionnel</div>
                  <div className="font-bold">{formatCurrency(data.resultatExceptionnel)}</div>
                </div>
                <div>
                  <div className="text-gray-600 text-lg">RÉSULTAT NET</div>
                  <div className="font-bold text-xl">{formatCurrency(data.resultatNet)}</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {data && (
        <div className="mt-6">
          <Card>
            <h2 className="text-lg font-bold text-gray-800 mb-4">
              <Icon name="IconClipboardList" className="text-indigo-600 mr-2" />
              Notes aux États Financiers
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Exercice comptable</span>
                <span className="font-semibold">{fiscalYears.find(f => f._id === fiscalYearId)?.label || '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Devise de présentation</span>
                <span className="font-semibold">Dinar Tunisien (TND)</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Base de préparation</span>
                <span className="font-semibold">Système Comptable des Entreprises (SCE) - Tunisie</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Conventions comptables</span>
                <span className="font-semibold">Comptabilité d'engagement - Méthode historique</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Date d'arrêté</span>
                <span className="font-semibold">{new Date().toLocaleDateString('fr-TN')}</span>
              </div>
              {statementType === 'balance-sheet' && (
                <>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Total Actif</span>
                    <span className="font-semibold">{formatCurrency(data.actif?.totalActif || 0)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Total Passif</span>
                    <span className="font-semibold">{formatCurrency(data.passif?.totalPassif || 0)}</span>
                  </div>
                </>
              )}
              {statementType === 'income-statement' && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Résultat Net de l'exercice</span>
                  <span className="font-semibold">{formatCurrency(data.resultatNet || 0)}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Méthode d'amortissement</span>
                <span className="font-semibold">Linéaire / Dégressif selon la nature des immobilisations</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Évaluation des stocks</span>
                <span className="font-semibold">Coût moyen pondéré (CMP)</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Provisions et dépréciations</span>
                <span className="font-semibold">Constituées conformément au SCE</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">TVA</span>
                <span className="font-semibold">Taux en vigueur : 19%, 13%, 7%, 0%</span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
